// Stream — the savegame byte stream (game/ht.h:8993-9043 + the misc.cpp helpers).
// Multi-byte values are raw little-endian memory images (the C++ Read/Write of
// word/dword on x86/wasm); strings are NUL-terminated; bulk maps use the 7-bit RLE
// (literal runs flagged 0x80, repeats of >= 3 encoded as count+byte).
//
// MemoryStream is the one concrete implementation the TS core needs: the embedder
// hands the bytes to whatever storage it likes (the C++ Host*SaveGameStream plumbing
// is platform shell).

export abstract class Stream {
  abstract Read(cb: number): Uint8Array; // returns cb bytes (short read -> failure)
  abstract Write(pv: Uint8Array): void;
  abstract IsSuccess(): boolean;
  Close(): void {}

  // ht.h:9006-9023 inlines
  ReadByte(): number {
    const b = this.Read(1);
    return b.length === 1 ? b[0] : 0;
  }

  ReadWord(): number {
    const b = this.Read(2);
    return b.length === 2 ? b[0] | (b[1] << 8) : 0;
  }

  ReadDword(): number {
    const b = this.Read(4);
    return b.length === 4 ? (b[0] | (b[1] << 8) | (b[2] << 16) | (b[3] << 24)) >>> 0 : 0;
  }

  WriteByte(b: number): void {
    this.Write(Uint8Array.of(b & 0xff));
  }

  WriteWord(w: number): void {
    this.Write(Uint8Array.of(w & 0xff, (w >> 8) & 0xff));
  }

  WriteDword(dw: number): void {
    this.Write(Uint8Array.of(dw & 0xff, (dw >> 8) & 0xff, (dw >> 16) & 0xff, (dw >> 24) & 0xff));
  }

  // misc.cpp:1142-1159 — fill at most cb chars; longer strings are capped and drained (sim-quirks B6).
  // The C++ overflow cap lands at psz[cb-2] (the `psz[cb - 1] = 0` runs after cb
  // post-decremented to -1 with psz one past the end), so the effective string keeps
  // cb-2 chars — reproduced bit-faithfully.
  ReadString(cb: number): string {
    let out = '';
    while (cb-- !== 0) {
      const ch = this.ReadByte();
      if (ch === 0) return out;
      out += String.fromCharCode(ch);
    }
    out = out.slice(0, Math.max(0, out.length - 2));
    while (this.ReadByte() !== 0) {
      /* drain */
    }
    return out;
  }

  // misc.cpp:1161-1164
  WriteString(psz: string): void {
    const b = new Uint8Array(psz.length + 1);
    for (let i = 0; i < psz.length; i++) b[i] = psz.charCodeAt(i) & 0xff;
    this.Write(b);
  }

  // misc.cpp:1166-1201 — literal runs separated by repeats of >= 3
  WriteBytesRLE(pb: Uint8Array): void {
    const cb = pb.length;
    let i = 0;
    while (i < cb) {
      const iRepeat = this.FindRLERepeat(pb, i, cb, 3);

      let cbLiteral = 0;
      if (iRepeat < 0) cbLiteral = cb - i;
      else if (iRepeat > i) cbLiteral = iRepeat - i;
      if (cbLiteral !== 0) {
        this.WriteRLEChunk(pb, i, cbLiteral, false);
        i += cbLiteral;
        continue;
      }

      let iT = iRepeat + 1;
      while (iT < cb && pb[iT] === pb[iRepeat]) iT++;
      const cbRepeat = iT - iRepeat;
      this.WriteRLEChunk(pb, i, cbRepeat, true);
      i += cbRepeat;
    }
  }

  // misc.cpp:1203-1218
  private FindRLERepeat(pb: Uint8Array, iStart: number, iMax: number, cbMin: number): number {
    for (; iStart < iMax; iStart++) {
      const bFind = pb[iStart];
      let cRepeat = 1;
      for (let iT = iStart + 1; iT < iMax; iT++) {
        if (pb[iT] !== bFind) break;
        cRepeat++;
        if (cRepeat >= cbMin) return iStart;
      }
    }
    return -1;
  }

  // misc.cpp:1220-1246
  private WriteRLEChunk(pb: Uint8Array, i: number, cb: number, fRepeat: boolean): void {
    if (!fRepeat) {
      while (cb !== 0) {
        const cbWrite = cb < 128 ? cb : 128;
        this.WriteByte(128 | (cbWrite - 1));
        this.Write(pb.subarray(i, i + cbWrite));
        i += cbWrite;
        cb -= cbWrite;
        if (!this.IsSuccess()) break;
      }
    } else {
      while (cb !== 0) {
        const cbWrite = cb < 128 ? cb : 128;
        this.WriteByte(0 | (cbWrite - 1));
        this.WriteByte(pb[i]);
        cb -= cbWrite;
        if (!this.IsSuccess()) break;
      }
    }
  }

  // misc.cpp:1248-1267 — fills exactly cb bytes into pb (a view into the caller's map)
  ReadBytesRLE(pb: Uint8Array): void {
    let cb = pb.length;
    let i = 0;
    while (cb !== 0) {
      const bT = this.ReadByte();
      const cbRun = (bT & ~128 & 0xff) + 1;
      if (bT & 128) {
        const data = this.Read(cbRun);
        pb.set(data, i);
      } else {
        const bRepeat = this.ReadByte();
        pb.fill(bRepeat, i, i + cbRun);
      }
      i += cbRun;
      cb -= cbRun;
      if (!this.IsSuccess()) break;
    }
  }
}

/** Growable in-memory stream: write then `bytes()`, or construct over a buffer to read. */
export class MemoryStream extends Stream {
  private buf: Uint8Array;
  private cb = 0; // bytes written (write mode) / total (read mode)
  private pos = 0; // read cursor
  private fSuccess = true;

  constructor(data?: Uint8Array) {
    super();
    if (data !== undefined) {
      this.buf = data;
      this.cb = data.length;
    } else {
      this.buf = new Uint8Array(4096);
    }
  }

  override Read(cbWant: number): Uint8Array {
    if (this.pos + cbWant > this.cb) {
      this.fSuccess = false;
      const out = this.buf.subarray(this.pos, this.cb);
      this.pos = this.cb;
      return out;
    }
    const out = this.buf.subarray(this.pos, this.pos + cbWant);
    this.pos += cbWant;
    return out;
  }

  override Write(pv: Uint8Array): void {
    if (this.cb + pv.length > this.buf.length) {
      const grown = new Uint8Array(Math.max(this.buf.length * 2, this.cb + pv.length));
      grown.set(this.buf.subarray(0, this.cb));
      this.buf = grown;
    }
    this.buf.set(pv, this.cb);
    this.cb += pv.length;
  }

  override IsSuccess(): boolean {
    return this.fSuccess;
  }

  bytes(): Uint8Array {
    return this.buf.slice(0, this.cb);
  }
  tell(): number {
    return this.pos;
  }
  tellWrite(): number {
    return this.cb;
  }
}

/**
 * Cross-validation dev hook: mark `cb` bytes ending `back` bytes before the current
 * write position as a dead/garbage span, so the save-parity masker
 * (test/harness/save-mask.mjs) can ignore exactly those bytes. Two kinds:
 *  - default: bytes where the TS save is 0 mask C++ heap/stack garbage — raw Message
 *    images (sim-quirks B15) and C++-ctor-uninitialized fields (sim-quirks B20);
 *  - both=true: zeroed on BOTH sides — C++ presentation code overwrites the field
 *    (e.g. Replicator::Draw mutates the saved animation, sim-quirks B21).
 * No-op unless a harness arms ctx.saveMsgSpans (bridged onto the stream as msgSpans
 * by Sim.saveState).
 */
export function markDeadSpan(
  pstm: unknown,
  cb: number,
  opts?: { both?: boolean; back?: number },
): void {
  const p = pstm as {
    tellWrite?: () => number;
    msgSpans?: { start: number; end: number; both?: boolean }[];
  };
  if (p.msgSpans !== undefined && p.tellWrite !== undefined) {
    const end = p.tellWrite() - (opts?.back ?? 0);
    p.msgSpans.push(opts?.both ? { start: end - cb, end, both: true } : { start: end - cb, end });
  }
}
